import { toast } from 'sonner'
import { useEffect, useMemo, useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { api } from '@/lib/api'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { EmptyState } from '@/components/shared/EmptyState'
import type { Alias, Domain, Mailbox } from '@/types'
import { Mail, Plus, Trash2, Loader2, Edit3, ChevronLeft, ChevronRight } from 'lucide-react'

interface AliasWithDomain extends Alias {
  domain?: { id: string; name: string }
}

// CHECKPOINT: PRD-02 FR-2.6.2 — Alias list resolves target IDs to mailbox emails.
function resolveTargetEmails(targetIds: string[], mailboxes: Mailbox[]): string[] {
  const byId = new Map(mailboxes.map((m) => [m.id, m.email]))
  return targetIds.map((id) => byId.get(id) || id)
}

export function Aliases() {
  const [aliases, setAliases] = useState<AliasWithDomain[]>([])
  const [domains, setDomains] = useState<Domain[]>([])
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)

  const [addOpen, setAddOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState({
    domain_id: '',
    source: '',
    targets: [] as string[],
    enabled: true,
  })

  const [editOpen, setEditOpen] = useState(false)
  const [editingAlias, setEditingAlias] = useState<AliasWithDomain | null>(null)
  const [editForm, setEditForm] = useState({ targets: [] as string[], enabled: true })
  const [savingEdit, setSavingEdit] = useState(false)

  const fetchAliases = async () => {
    try {
      const res = await api.get(`/aliases?page=${page}&per_page=20`)
      setAliases(res.data.aliases || [])
      setTotal(res.data.pagination?.total || 0)
      setTotalPages(res.data.pagination?.total_pages || 1)
    } catch {
      toast.error('Failed to load aliases')
    } finally {
      setLoading(false)
    }
  }

  const fetchAllMailboxes = async () => {
    const all: Mailbox[] = []
    let page = 1
    let totalPages = 1
    try {
      while (page <= totalPages) {
        const res = await api.get(`/mailboxes?page=${page}&per_page=100`)
        const batch = res.data.mailboxes || []
        const pagination = res.data.pagination || {}
        all.push(...batch)
        totalPages = pagination.total_pages || 1
        page += 1
        if (batch.length === 0) break
      }
    } catch {
      // ignore
    }
    return all
  }

  const fetchDomainsAndMailboxes = async () => {
    try {
      const [dRes, allMailboxes] = await Promise.all([
        api.get('/domains?page=1&per_page=100'),
        fetchAllMailboxes(),
      ])
      setDomains((dRes.data.domains || []).filter((d: Domain) => d.status === 'active'))
      setMailboxes(allMailboxes)
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    fetchAliases()
  }, [page])

  useEffect(() => {
    fetchDomainsAndMailboxes()
  }, [])

  // CHECKPOINT: PRD-10 FR-10.13.2 — Reset creation form state when dialog opens.
  useEffect(() => {
    if (addOpen) {
      setForm({ domain_id: domains[0]?.id || '', source: '', targets: [], enabled: true })
    }
  }, [addOpen, domains])

  // CHECKPOINT: PRD-02 FR-2.6.1 — Mailboxes available for aliasing are limited to
  // the selected domain; targets are stored as mailbox IDs.
  const availableMailboxes = useMemo(() => {
    if (!form.domain_id) return []
    return mailboxes.filter((m) => m.domain_id === form.domain_id && m.status === 'active')
  }, [form.domain_id, mailboxes])

  const handleCreate = async () => {
    if (!form.domain_id || !form.source.trim() || form.targets.length === 0) return
    setCreating(true)
    try {
      await api.post('/aliases', {
        domain_id: form.domain_id,
        source: form.source.trim(),
        targets: form.targets,
        enabled: form.enabled,
      })
      setForm({ domain_id: '', source: '', targets: [], enabled: true })
      setAddOpen(false)
      fetchAliases()
      toast.success('Alias created')
    } catch (err: any) {
      // CHECKPOINT: PRD-10 FR-10.13.5 — Use toast for async operation errors
      toast.error(err.response?.data?.detail?.message || 'Failed to create alias')
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this alias?')) return
    try {
      await api.delete(`/aliases/${id}`)
      fetchAliases()
      toast.success('Alias deleted')
    } catch {
      toast.error('Failed to delete alias')
    }
  }

  const openEdit = (alias: AliasWithDomain) => {
    setEditingAlias(alias)
    // Preserve any selected targets even if the mailbox is no longer active so
    // the user can see what was previously configured.
    setEditForm({
      targets: alias.targets,
      enabled: alias.enabled,
    })
    setEditOpen(true)
  }

  const handleEditSave = async () => {
    if (!editingAlias || editForm.targets.length === 0) return
    setSavingEdit(true)
    try {
      await api.put(`/aliases/${editingAlias.id}`, {
        targets: editForm.targets,
        enabled: editForm.enabled,
      })
      toast.success('Alias updated')
      setEditOpen(false)
      fetchAliases()
    } catch (err: any) {
      toast.error(err.response?.data?.detail?.message || 'Failed to update alias')
    } finally {
      setSavingEdit(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 w-32 bg-accent rounded" />
        <div className="h-64 bg-accent rounded-xl" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Aliases</h1>
          <p className="text-sm text-muted-foreground mt-1">Forward one address to one or more mailboxes.</p>
        </div>
        <Button
          onClick={() => setAddOpen(true)}
          className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
        >
          <Plus className="mr-2 h-4 w-4" />
          New Alias
        </Button>
      </div>

      {aliases.length === 0 ? (
        <EmptyState
          title="No aliases yet"
          description="Create an alias to forward emails from one address to multiple destinations."
          icon={<Mail className="h-5 w-5" />}
          action={
            <Button onClick={() => setAddOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              New Alias
            </Button>
          }
        />
      ) : (
        <>
          <div className="grid gap-3">
            {aliases.map((alias) => {
              const targetEmails = resolveTargetEmails(alias.targets, mailboxes)
              return (
                <Card key={alias.id} className="hover:bg-accent/20 transition-colors">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="h-10 w-10 rounded-lg bg-accent flex items-center justify-center shrink-0">
                        <Mail className="h-5 w-5 text-violet-400" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold truncate">{alias.source}</p>
                          {alias.is_catch_all && (
                            <span className="text-[10px] bg-amber-500/10 text-amber-400 px-1.5 py-0.5 rounded">
                              catch-all
                            </span>
                          )}
                          <StatusBadge status={alias.enabled} />
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-muted-foreground truncate">
                            → {targetEmails.join(', ')}
                          </span>
                        </div>
                        {alias.domain && (
                          <p className="text-[10px] text-muted-foreground mt-0.5">{alias.domain.name}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(alias)} title="Edit alias">
                        <Edit3 className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(alias.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">{total} total</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">Page {page}</span>
              <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Create Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Alias</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm font-medium">Domain</label>
              <select
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={form.domain_id}
                onChange={(e) => setForm({ ...form, domain_id: e.target.value, targets: [] })}
              >
                <option value="">Select a domain</option>
                {domains.map((domain) => (
                  <option key={domain.id} value={domain.id}>
                    {domain.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Source</label>
              <Input
                placeholder="e.g. support"
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Use a local part like "support" or a catch-all "@domain.com"
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Target Mailboxes</label>
              <select
                multiple
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm min-h-[120px]"
                value={form.targets}
                onChange={(e) => {
                  const opts = Array.from(e.target.selectedOptions).map((o) => o.value)
                  setForm({ ...form, targets: opts })
                }}
              >
                {availableMailboxes.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.email}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-muted-foreground mt-1">Hold Ctrl/Cmd to select multiple (max 10)</p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                className="rounded border-border"
              />
              Enabled
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !form.domain_id || !form.source.trim() || form.targets.length === 0}
            >
              {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Alias
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Alias</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm font-medium">{editingAlias?.source}</p>
            <div>
              <label className="text-sm font-medium">Target Mailboxes</label>
              <select
                multiple
                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm min-h-[120px]"
                value={editForm.targets}
                onChange={(e) => {
                  const opts = Array.from(e.target.selectedOptions).map((o) => o.value)
                  setEditForm({ ...editForm, targets: opts })
                }}
              >
                {mailboxes
                  .filter((m) => m.domain_id === editingAlias?.domain?.id)
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.email}
                    </option>
                  ))}
              </select>
              <p className="text-[10px] text-muted-foreground mt-1">Hold Ctrl/Cmd to select multiple (max 10)</p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={editForm.enabled}
                onChange={(e) => setEditForm({ ...editForm, enabled: e.target.checked })}
                className="rounded border-border"
              />
              Enabled
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditSave} disabled={savingEdit || editForm.targets.length === 0}>
              {savingEdit && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
